import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Check, ArrowLeft, Phone, MessageCircle, Gift } from "lucide-react";
import api, { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

const themes = {
  amber: { text: "text-amber-600", btn: "bg-amber-500 hover:bg-amber-600", glow: "from-amber-50" },
  pink: { text: "text-pink-600", btn: "bg-pink-500 hover:bg-pink-600", glow: "from-pink-50" },
  blue: { text: "text-sky-600", btn: "bg-sky-600 hover:bg-sky-700", glow: "from-sky-50" },
};

export default function OfferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/offers/${id}`)
      .then((res) => setOffer(res.data))
      .catch(() => navigate("/oferte"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-sky-200 border-t-sky-600" />
      </div>
    );
  }
  if (!offer) return null;

  const t = themes[offer.theme] || themes.blue;
  const addOffer = () =>
    addItem({ id: `offer-${offer._id}`, name: offer.title, price: offer.price, image: offer.image }, 1);

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-12" data-testid="offer-detail-page">
      <Link to="/oferte" className="mb-8 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-sky-600">
        <ArrowLeft className="h-4 w-4" /> Înapoi la oferte
      </Link>

      <div className="grid gap-12 lg:grid-cols-2">
        <div className={`overflow-hidden rounded-3xl bg-gradient-to-b ${t.glow} to-white`}>
          <img src={offer.image} alt={offer.title} className="w-full object-contain" />
        </div>

        <div className="flex flex-col justify-center">
          <span className="label-caps inline-flex w-fit items-center gap-2 rounded-full bg-emerald-100 px-4 py-1.5 text-emerald-700">
            <Gift className="h-3.5 w-3.5" /> Promoție specială online
          </span>
          <h1 className="mt-4 font-display text-3xl font-black tracking-tight text-slate-900 lg:text-4xl" data-testid="offer-title">
            {offer.subtitle}
          </h1>
          <div className="mt-4 flex items-end gap-3">
            <span className="font-display text-4xl font-black text-slate-900" data-testid="offer-price">{formatPrice(offer.price)}</span>
            {offer.old_price && <span className="mb-1 text-xl text-slate-400 line-through">{formatPrice(offer.old_price)}</span>}
          </div>
          <p className="mt-1 font-semibold text-emerald-600">Economisești {formatPrice(offer.old_price - offer.price)}</p>
          <p className="mt-5 leading-relaxed text-slate-600">{offer.description}</p>

          <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
            {offer.features.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-100 text-emerald-600"><Check className="h-3 w-3" /></span>
                {f}
              </li>
            ))}
          </ul>

          <Button size="lg" className={`mt-8 w-full rounded-full text-base text-white ${t.btn}`} onClick={addOffer} data-testid="offer-add-to-cart">
            <ShoppingCart className="mr-2 h-5 w-5" /> Adaugă pachetul în coș
          </Button>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <a
              href={`https://wa.me/40734154845?text=${encodeURIComponent(`Bună ziua! Aș dori să comand pachetul: ${offer.title}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="offer-whatsapp"
              className="flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-emerald-600"
            >
              <MessageCircle className="h-5 w-5" /> Comandă pe WhatsApp
            </a>
            <a
              href="tel:+40734154845"
              data-testid="offer-call"
              className="flex items-center justify-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-base font-semibold text-slate-700 transition-colors hover:border-sky-300 hover:text-sky-600"
            >
              <Phone className="h-5 w-5" /> Sună-ne: +40 734 154 845
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
